import { formatInTimeZone } from 'date-fns-tz';
import { supabase } from '../lib/supabaseClient';
import { compressImage } from '../lib/compressImage';
import { documentAlerts, type DocumentAlert, type WorkerDocument } from '../domain/documents';

export interface ActiveDocument extends WorkerDocument {
  workerName: string;
}

/**
 * Documentos laborales vigentes (`active = true`) de todos los trabajadores visibles
 * por RLS. Devuelve [] si la tabla `worker_documents` aún no existe.
 */
export async function listActiveDocuments(): Promise<ActiveDocument[]> {
  const { data, error } = await supabase
    .from('worker_documents')
    .select('worker_id, type, label, expiry_date, worker:profiles(first_names, last_names)')
    .eq('active', true);
  if (error) {
    if (error.code === '42P01') {
      console.warn('Tabla worker_documents aún no creada; sin alertas de documentos.');
      return [];
    }
    throw error;
  }
  type Raw = {
    worker_id: string;
    type: WorkerDocument['type'];
    label: string | null;
    expiry_date: string | null;
    worker: { first_names: string; last_names: string } | null;
  };
  return ((data ?? []) as unknown as Raw[]).map((d) => ({
    workerId: d.worker_id,
    type: d.type,
    label: d.label,
    expiryDate: d.expiry_date,
    workerName: d.worker ? `${d.worker.first_names} ${d.worker.last_names}` : '—',
  }));
}

// --- Gestión de documentos laborales (Task 36) ---

const TZ_LIMA = 'America/Lima';

export type DocumentType = 'carnet_sanidad' | 'certificado_unico_laboral' | 'otro';

export const DOCUMENT_TYPE_LABEL: Record<DocumentType, string> = {
  carnet_sanidad: 'Carnet de sanidad',
  certificado_unico_laboral: 'Certificado único laboral',
  otro: 'Otro documento',
};

export interface WorkerDocumentRow {
  id: string;
  worker_id: string;
  type: DocumentType;
  label: string | null;
  file_path: string;
  issue_date: string | null;
  expiry_date: string | null;
  active: boolean;
  created_at: string;
}

/** Documento vigente con su alerta ya calculada (`null` = vigente o sin vencimiento). */
export interface DocumentWithAlert {
  id: string;
  type: DocumentType;
  label: string | null;
  file_path: string;
  issue_date: string | null;
  expiry_date: string | null;
  alert: DocumentAlert | null;
}

export interface WorkerDocuments {
  worker_id: string;
  dni: string;
  first_names: string;
  last_names: string;
  documents: DocumentWithAlert[];
  alerts: DocumentAlert[];
  /**
   * Campañas y tiendas donde la persona tiene un turno VIVO (propuesta o aprobada).
   *
   * Se traen para poder filtrar por ellas —pedido del usuario del 10-set: *«colocar un campo para
   * filtrar por escritura como tienen otras pantallas: nombre, dni, tienda, documento, campaña»*—
   * y **se muestran en la fila**, no solo se buscan. Un filtro que calza contra datos invisibles
   * deja al usuario preguntándose por qué apareció esa fila.
   *
   * Van vacías cuando la persona no tiene ningún turno vivo, que es la mayoría del padrón: la
   * pantalla lista a todos los recursos activos, no solo a los programados.
   */
  campanias: string[];
  tiendas: string[];
}

/** Historial COMPLETO (vigentes e históricos) de un trabajador, del más nuevo al más viejo. */
export async function listWorkerDocuments(workerId: string): Promise<WorkerDocumentRow[]> {
  const { data, error } = await supabase
    .from('worker_documents')
    .select('id, worker_id, type, label, file_path, issue_date, expiry_date, active, created_at')
    .eq('worker_id', workerId)
    .order('created_at', { ascending: false });
  if (error) throw new Error(error.message);
  return (data ?? []) as WorkerDocumentRow[];
}

/**
 * Trabajadores activos con rol `recurso` y sus documentos VIGENTES (`active = true`),
 * cada uno con la alerta calculada por `documentAlerts` (hoy en America/Lima, warnDays default 30).
 * Los trabajadores sin documentos vienen con `documents: []` para poder listarlos igual.
 */
export async function listDocumentsWithAlerts(): Promise<WorkerDocuments[]> {
  const { data, error } = await supabase
    .from('profiles')
    .select(
      'id, dni, first_names, last_names, ' +
      'worker_documents(id, type, label, file_path, issue_date, expiry_date, active)',
    )
    .eq('status', 'activo')
    .contains('roles', ['recurso'])
    .eq('worker_documents.active', true)
    .order('last_names', { ascending: true });
  if (error) throw new Error(error.message);

  interface Raw {
    id: string;
    dni: string;
    first_names: string;
    last_names: string;
    worker_documents:
      | { id: string; type: DocumentType; label: string | null; file_path: string;
          issue_date: string | null; expiry_date: string | null; active: boolean }[]
      | null;
  }
  const rows = (data ?? []) as unknown as Raw[];

  const dondeTrabaja = await campaniasYTiendasPorTrabajador();

  const today = formatInTimeZone(new Date(), TZ_LIMA, 'yyyy-MM-dd');
  const input: WorkerDocument[] = rows.flatMap((p) =>
    (p.worker_documents ?? []).map((d) => ({
      workerId: p.id,
      type: d.type,
      label: d.label,
      expiryDate: d.expiry_date,
    })),
  );
  // Solo hay un documento activo por (trabajador, tipo): al renovar, el anterior pasa a active=false.
  const byKey = new Map<string, DocumentAlert>();
  for (const a of documentAlerts(input, today)) byKey.set(`${a.workerId}|${a.type}`, a);

  return rows.map((p) => {
    const documents: DocumentWithAlert[] = (p.worker_documents ?? []).map((d) => ({
      id: d.id,
      type: d.type,
      label: d.label,
      file_path: d.file_path,
      issue_date: d.issue_date,
      expiry_date: d.expiry_date,
      alert: byKey.get(`${p.id}|${d.type}`) ?? null,
    }));
    const donde = dondeTrabaja.get(p.id);
    return {
      worker_id: p.id,
      dni: p.dni,
      first_names: p.first_names,
      last_names: p.last_names,
      campanias: donde ? [...donde.campanias].sort() : [],
      tiendas: donde ? [...donde.tiendas].sort() : [],
      documents,
      alerts: documents
        .map((d) => d.alert)
        .filter((a): a is DocumentAlert => a !== null),
    };
  });
}

/**
 * Dónde trabaja cada persona: sus campañas y sus tiendas, por turnos VIVOS.
 *
 * Se consulta desde `positions` y no desde `assignments` por lo mismo que `listAssignedPositions`:
 * la campaña y el PDV viven en la posición, así que desde acá se llega a los dos con un embed
 * directo en vez de uno anidado. `assignments!inner` deja fuera las posiciones sin nadie.
 *
 * Si esta consulta falla, **no se cae la pantalla**: se devuelve un mapa vacío y la lista de
 * documentos —que es lo que la pantalla vino a mostrar— aparece igual, solo sin campaña ni tienda
 * para filtrar. Un dato de apoyo no puede tumbar el dato principal.
 */
async function campaniasYTiendasPorTrabajador(): Promise<
  Map<string, { campanias: Set<string>; tiendas: Set<string> }>
> {
  const mapa = new Map<string, { campanias: Set<string>; tiendas: Set<string> }>();
  const { data, error } = await supabase
    .from('positions')
    .select('campaign:campaigns(client, brand), pdv:pdv(name), assignments!inner(worker_id, status)')
    .in('assignments.status', ['propuesta', 'aprobada']);
  if (error) {
    // eslint-disable-next-line no-console -- se sigue sin campaña ni tienda; hay que poder saberlo
    console.warn('No se pudieron traer campañas y tiendas por trabajador:', error.message);
    return mapa;
  }

  interface Raw {
    campaign: { client: string; brand: string } | null;
    pdv: { name: string } | null;
    assignments: { worker_id: string; status: string }[] | null;
  }
  for (const fila of (data ?? []) as unknown as Raw[]) {
    const campania = fila.campaign ? `${fila.campaign.client} — ${fila.campaign.brand}` : null;
    for (const a of fila.assignments ?? []) {
      let acc = mapa.get(a.worker_id);
      if (!acc) { acc = { campanias: new Set(), tiendas: new Set() }; mapa.set(a.worker_id, acc); }
      if (campania) acc.campanias.add(campania);
      if (fila.pdv?.name) acc.tiendas.add(fila.pdv.name);
    }
  }
  return mapa;
}

/** URL firmada de 60 segundos para un documento del bucket privado `photos`. */
export async function getDocumentUrl(path: string): Promise<string> {
  const { data, error } = await supabase.storage.from('photos').createSignedUrl(path, 60);
  if (error || !data) throw new Error(error?.message ?? 'No se pudo abrir el documento.');
  return data.signedUrl;
}

/**
 * Sube (o renueva) un documento laboral:
 * 1) comprime si es imagen; 2) guarda en documents/<dni>/<type>-<timestamp>.<ext>;
 * 3) llama a la RPC `renew_worker_document`, que en UNA transacción desactiva el anterior
 *    del mismo tipo, inserta la fila nueva y escribe `audit_log`.
 * El paso a `active = false` NO se hace desde el cliente: la política `worker_documents_update`
 * está limitada a admin, así que un recurso no podría desactivar su propio documento.
 */
export async function uploadWorkerDocument(
  workerId: string,
  dni: string,
  type: DocumentType,
  file: File,
  issueDate: string,
  expiryDate: string,
  label?: string | null,
): Promise<WorkerDocumentRow> {
  const isImage = file.type.startsWith('image/');
  const body: Blob = isImage ? await compressImage(file) : file;
  const nameExt = file.name.includes('.') ? file.name.split('.').pop()!.toLowerCase() : '';
  const ext = isImage ? 'jpg' : nameExt || 'pdf';
  const contentType = isImage ? 'image/jpeg' : file.type || 'application/pdf';
  const path = `documents/${dni}/${type}-${Date.now()}.${ext}`;

  const up = await supabase.storage.from('photos').upload(path, body, { contentType, upsert: false });
  if (up.error) throw new Error('No se pudo subir el archivo: ' + up.error.message);

  const { data, error } = await supabase.rpc('renew_worker_document', {
    p_worker: workerId,
    p_type: type,
    p_file_path: path,
    p_issue: issueDate || null,
    p_expiry: expiryDate || null,
    p_label: label && label.trim() !== '' ? label.trim() : null,
  });

  if (error) {
    // El archivo ya subió pero la fila no se creó: no dejar el objeto huérfano.
    await supabase.storage.from('photos').remove([path]);
    const m = error.message ?? '';
    if (m.includes('no_autorizado')) {
      throw new Error('Solo el jefe de operaciones puede registrar documentos de otro trabajador.');
    }
    if (m.includes('vencimiento_anterior_a_emision')) {
      throw new Error('La fecha de vencimiento no puede ser anterior a la de emisión.');
    }
    if (m.includes('trabajador_inexistente')) {
      throw new Error('El trabajador ya no existe.');
    }
    if (m.includes('tipo_invalido') || m.includes('datos_incompletos')) {
      throw new Error('Los datos del documento están incompletos o el tipo no es válido.');
    }
    throw new Error('No se pudo registrar el documento: ' + m);
  }

  return data as WorkerDocumentRow;
}
